import { useState, useEffect, useMemo } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { categories, comics } from '@/mocks/comics';

const STATUS_FILTERS = ['全部', '连载中', '已完结'] as const;
const SORT_OPTIONS = [
  { label: '最新更新', value: 'update' },
  { label: '最受欢迎', value: 'popular' },
  { label: '评分最高', value: 'rating' },
] as const;

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const [activeStatus, setActiveStatus] = useState<string>('全部');
  const [activeSort, setActiveSort] = useState<string>('update');
  const [showMobileFilter, setShowMobileFilter] = useState(false);

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [location.pathname]);

  useEffect(() => {
    setActiveStatus('全部');
    setActiveSort('update');
  }, [slug]);

  const filteredComics = useMemo(() => {
    let result = comics.filter((c) => c.category === currentCategory.slug);

    if (activeStatus !== '全部') {
      result = result.filter((c) => c.status === activeStatus);
    }

    switch (activeSort) {
      case 'popular':
        result = [...result].sort((a, b) => b.views - a.views);
        break;
      case 'rating':
        result = [...result].sort((a, b) => b.rating - a.rating);
        break;
      case 'update':
      default:
        result = [...result].sort((a, b) => new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime());
        break;
    }

    return result;
  }, [currentCategory.slug, activeStatus, activeSort]);

  const categoryNameMap: Record<string, string> = {};
  categories.forEach((c) => { categoryNameMap[c.slug] = c.name; });

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '分类浏览', href: '/category/lianai' },
    { label: currentCategory.name },
  ];

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name}漫画 - yy漫画`,
    description: `yy漫画${currentCategory.name}分类，汇集海量优质${currentCategory.name}漫画资源，正版授权，每日更新。`,
    url: `https://yytoon.github.io/category/${currentCategory.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: 'yy漫画',
      url: 'https://yytoon.github.io',
    },
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: filteredComics.map((c, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'ComicStory',
          name: c.title,
          author: { '@type': 'Person', name: c.author },
          url: `https://yytoon.github.io/article/${c.id}`,
        },
      })),
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(categorySchema)}</script>

      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />

            <div className="px-4 md:px-6 pb-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100">
                  <i className={`${currentCategory.icon} text-xl text-primary-600`}></i>
                </div>
                <div>
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                    {currentCategory.name}漫画
                  </h1>
                  <p className="text-sm text-foreground-500 mt-1">{currentCategory.description}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex gap-8 py-8">
            <aside className="hidden lg:block w-48 shrink-0">
              <nav className="sticky top-24">
                <h4 className="text-sm font-semibold text-foreground-900 mb-3">全部分类</h4>
                <ul className="space-y-1">
                  {categories.map((cat) => (
                    <li key={cat.slug}>
                      <Link
                        to={`/category/${cat.slug}`}
                        className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer ${
                          cat.slug === currentCategory.slug
                            ? 'bg-primary-50 text-primary-600 font-medium'
                            : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                        }`}
                      >
                        <i className={`${cat.icon} text-base`}></i>
                        <span>{cat.name}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            </aside>

            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-2 flex-wrap">
                  {STATUS_FILTERS.map((s) => (
                    <button
                      key={s}
                      onClick={() => setActiveStatus(s)}
                      className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                        activeStatus === s
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>

                <div className="flex items-center gap-3">
                  <div className="hidden sm:flex items-center gap-2">
                    {SORT_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setActiveSort(opt.value)}
                        className={`text-sm transition-colors cursor-pointer whitespace-nowrap ${
                          activeSort === opt.value
                            ? 'text-primary-600 font-medium'
                            : 'text-foreground-500 hover:text-foreground-800'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>

                  <button
                    className="sm:hidden flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-background-100 text-sm text-foreground-700 cursor-pointer"
                    onClick={() => setShowMobileFilter(!showMobileFilter)}
                  >
                    <i className="ri-filter-3-line text-sm"></i> 筛选
                  </button>
                </div>
              </div>

              {showMobileFilter && (
                <div className="sm:hidden mb-4 p-3 rounded-lg bg-background-100 flex items-center gap-2 flex-wrap">
                  {SORT_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => { setActiveSort(opt.value); setShowMobileFilter(false); }}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                        activeSort === opt.value
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-200 text-foreground-600'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}

              {filteredComics.length === 0 ? (
                <div className="text-center py-20">
                  <i className="ri-inbox-line text-5xl text-foreground-300"></i>
                  <p className="mt-4 text-foreground-500">暂无符合条件的漫画</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                  {filteredComics.map((comic) => (
                    <ComicCard key={comic.id} comic={comic} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}